import { useSearchParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import { Checkbox, ConfigProvider } from "antd"
import { useUpdateFurnitureStatus } from "hooks/api/quotation/quotations.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { spliceFurnitureIntoCache } from "helpers/quotationCache.helper"
import AnnotationService from "services/annotations.service"
import { FurnitureModelLabel } from "components/ui/customWebviewer/FurnitureModelLabel/FurnitureModelLabel"
import { FurnitureStatusIcon } from "components/ui/customWebviewer/ElevationReferencesSidebar/icons/FurnitureStatusIcon"
import type { TErefWithFurniture, TFurniture, TFurnitureStatus } from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import style from "./Objects.module.scss"

const { objects, label, checkboxGroup, checkboxItem, rowButton } = style

type Props = {
	eref: TErefWithFurniture
	onFurnitureClick?: (furniture: TFurniture) => void
}

export const Objects = ({ eref, onFurnitureClick }: Props) => {
	const [searchParams] = useSearchParams()
	const quotationId = Number(searchParams.get("quotationID"))
	const queryClient = useQueryClient()
	const { formatError } = useApiErrorFormatter()
	const { mutate: updateFurnitureStatus, isPending, variables } = useUpdateFurnitureStatus()
	const pendingFurnitureId = isPending ? variables?.furnitureId : null

	if (!eref.furniture.length) return null

	const toggleQuotation = (item: TFurniture) => {
		if (!Number.isFinite(quotationId) || !quotationId) return
		const currentStatus: TFurnitureStatus = item.status ?? "work_in_progress"
		const next: TFurnitureStatus = currentStatus === "in_quotation" ? "reviewed" : "in_quotation"

		updateFurnitureStatus(
			{
				quotationId,
				furnitureId: item.id,
				body: { version: item.version, status: next },
			},
			{
				onSuccess: (response) => {
					const updated = response?.data as TFurniture | undefined
					if (!updated) return
					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) =>
						spliceFurnitureIntoCache(cached, updated),
					)
					AnnotationService.updateFurnitureEntityStatus(item.id, next)
				},
				onError: (e) => {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				},
			},
		)
	}

	return (
		<div className={objects}>
			<span className={label}>Objects</span>
			<div className={checkboxGroup}>
				{eref.furniture.map((item) => {
					const status: TFurnitureStatus = item.status ?? "work_in_progress"
					return (
						<div key={item.id} className={checkboxItem}>
							<button type="button" className={rowButton} onClick={() => onFurnitureClick?.(item)}>
								<FurnitureStatusIcon status={status} />
								<FurnitureModelLabel item={item} />
							</button>
							{/* Scoped green checkbox (mirrors --clr-quotationGreen); Ant theme tokens require a literal color */}
							<ConfigProvider theme={{ token: { colorPrimary: "#00A651" } }}>
								<Checkbox
									checked={status === "in_quotation"}
									disabled={pendingFurnitureId === item.id}
									onChange={() => toggleQuotation(item)}
								/>
							</ConfigProvider>
						</div>
					)
				})}
			</div>
		</div>
	)
}
